import { createFileRoute, Link } from "@tanstack/react-router";
import { UpgradePrompt } from "@/components/site/UpgradePrompt";
import { useQuery } from "@tanstack/react-query";
import { listDomains } from "@/lib/academy.functions";
import { LivingBackdrop } from "@/academy/engines/living/LivingBackdrop";
import { EnergyNode } from "@/academy/engines/living/EnergyNode";
import { ArrowRight, Sparkles } from "lucide-react";
import { ACADEMY_ID, breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

const TITLE = "Buddha KA Academy — Consciousness Architecture";
const DESCRIPTION =
  "The Buddha KA Academy: a living observatory for the study of consciousness — Human Design, the KAfrequency framework, energy mechanics, and university-depth lessons with labeled provenance.";

export const Route = createFileRoute("/academy/")({
  component: AcademyHome,
  head: () => ({
    ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/academy" }),
    scripts: [
      ldScript(
        pageNode({
          type: "CollectionPage",
          id: ACADEMY_ID,
          url: "https://buddhaka.com/academy",
          name: TITLE,
          description: DESCRIPTION,
          about: [personRef(), orgRef()],
          extra: { publisher: orgRef(), creator: personRef() },
        }),
      ),
      ldScript(breadcrumbs([["Academy", "/academy"]])),
    ],
  }),
});


type DomainHueMap = Record<string, number>;
const DOMAIN_HUE: DomainHueMap = {
  "human-design": 44,
  "kafrequency": 300,
};

function AcademyHome() {
  const { data: domains = [], isLoading } = useQuery({
    queryKey: ["academy", "domains"],
    queryFn: () => listDomains(),
  });

  return (
    <div className="relative min-h-dvh bg-obsidian text-foreground">
      {/* Hero observatory */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0"><LivingBackdrop hue={44} density={90} /></div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:py-32">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">The Academy</p>
          <h1 className="mt-3 font-serif text-4xl leading-tight sm:text-6xl">
            An observatory for<br/>living intelligence.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-foreground/80">
            Study your energetic architecture through interactive maps, guided paths and a
            growing library. Two domains open today. More frameworks arrive without ever
            leaving this observatory.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/academy/kafrequency" 
              className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">
              Enter KAfrequency Academy <ArrowRight size={16} />
            </Link>
            <Link to="/academy/$domain" params={{ domain: "human-design" }}
              className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-2.5 text-sm">
              Human Design
            </Link>
          </div>
        </div>
      </section>

      {/* Domain portals as breathing orbs */}
      <section className="mx-auto max-w-6xl px-4 pb-24 sm:px-6">
        <div className="mb-8 flex items-end justify-between">
          <h2 className="font-serif text-2xl text-gold-luminous">Domains</h2>
          <Link to="/academy/dashboard" className="text-sm text-gold hover:underline">
            Your dashboard →
          </Link>
        </div>
        {isLoading && <p className="text-sm text-foreground/60">Awakening the field…</p>}
        <div className="grid gap-6 sm:grid-cols-2">
          {domains.map((d) => {
            const hue = DOMAIN_HUE[d.slug] ?? 44;
            return (
              <Link key={d.id} to="/academy/$domain" params={{ domain: d.slug }}
                className="group relative overflow-hidden rounded-2xl border hairline p-6 transition hover:border-gold/60">
                <div className="absolute inset-0 opacity-70">
                  <LivingBackdrop hue={hue} density={40} />
                </div>
                <div className="relative z-10 flex items-start gap-5">
                  <svg viewBox="0 0 100 100" className="h-24 w-24 shrink-0" aria-hidden>
                    <EnergyNode cx={50} cy={50} r={38} hue={hue} active defined />
                  </svg>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Sparkles size={16} className="text-gold" />
                      <h3 className="font-serif text-xl">{d.name}</h3>
                    </div>
                    {d.tagline && <p className="mt-1 text-xs uppercase tracking-widest text-foreground/60">{d.tagline}</p>}
                    {d.description && <p className="mt-3 text-sm text-foreground/80">{d.description}</p>}
                    <span className="mt-4 inline-flex items-center gap-1 text-sm text-gold transition-all group-hover:gap-2">
                      Enter <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <UpgradePrompt context="academy" />
      </div>
    </div>
  );
}
